/**
 * Theme context — dark/light app-wide toggle.
 *
 * Extracted from AuthContext on 2026-05-23 (post-audit batch). Theme is a
 * device-scoped UI preference that the auth layer should not own; mixing
 * them meant `useAuth()` re-rendered any subtree that only needed the
 * theme value, and every login flow had to remember to pass `setTheme`
 * around. Split makes both contexts smaller and the dependency graph
 * obvious.
 *
 * Behavior preserved from the previous inline impl:
 *   - persists to localStorage under "altiv_theme"
 *   - applies a `.light-theme` class to <html> (CSS hooks live there)
 *   - keeps the iOS `theme-color` meta in sync so safe-area slivers
 *     don't show a mismatched color against full-bleed modals
 *
 * Behavior intentionally CHANGED:
 *   - Logging in no longer force-resets the theme to "dark". Theme is
 *     personal to the device — preserving the user's last choice across
 *     login sessions matches every other SaaS we've used and surprises
 *     no one. Multi-user shared devices can still toggle in one click.
 */

import React, { createContext, useCallback, useContext, useEffect, useState } from "react";

const ThemeContext = createContext(null);

const STORAGE_KEY = "altiv_theme";
const DEFAULT_THEME = "dark";

function _applyThemeColorMeta(newTheme) {
  // iOS pinta áreas del sistema (status bar, gesture bar abajo, safe-areas)
  // con este color cuando la página no las cubre. Si el modal full-bleed
  // deja una sliver fuera de su `100dvh`, esa sliver toma este color.
  // Ajustarlo al tema activo evita franjas que no matcheen.
  const meta =
    typeof document !== "undefined" ? document.querySelector('meta[name="theme-color"]') : null;
  if (meta) meta.setAttribute("content", newTheme === "light" ? "#FFFFFF" : "#0f172a");
}

function _readInitialTheme() {
  if (typeof localStorage === "undefined") return DEFAULT_THEME;
  try {
    return localStorage.getItem(STORAGE_KEY) || DEFAULT_THEME;
  } catch {
    return DEFAULT_THEME;
  }
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(_readInitialTheme);

  const setTheme = useCallback((newTheme) => {
    try {
      localStorage.setItem(STORAGE_KEY, newTheme);
    } catch {
      // localStorage may throw under Safari private mode or quota — DOM
      // state still flips so the user sees their choice take effect,
      // it just won't persist across reloads.
    }
    setThemeState(newTheme);
    if (typeof document !== "undefined") {
      if (newTheme === "light") {
        document.documentElement.classList.add("light-theme");
      } else {
        document.documentElement.classList.remove("light-theme");
      }
    }
    _applyThemeColorMeta(newTheme);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(theme === "dark" ? "light" : "dark");
  }, [theme, setTheme]);

  // Apply initial theme to the document on mount. Without this, a user
  // whose stored preference is "light" would see dark briefly until the
  // first toggle (FOUC).
  useEffect(() => {
    if (theme === "light" && typeof document !== "undefined") {
      document.documentElement.classList.add("light-theme");
    }
    _applyThemeColorMeta(theme);
    // Run once on mount only — subsequent changes go through `setTheme`
    // which already updates the DOM.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}
